// Fichier : src/components/DepassementsParMois.jsx

import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

// ✅ ÉTAPE 1 : Le composant accepte maintenant les deux listes de dépassements
const DepassementsParMois = ({ billableExceedances, allExceedances }) => {
  const [openMonth, setOpenMonth] = useState(null);

  const toggleMonth = (moisKey) => {
    setOpenMonth(openMonth === moisKey ? null : moisKey);
  };

  const depassementsGroupes = useMemo(() => {
    // ✅ ÉTAPE 2 : On utilise "allExceedances" pour regrouper TOUS les événements
    const allEvents = allExceedances || [];
    const billableEvents = billableExceedances || [];
    
    const groupes = {};

    // On regroupe tous les événements par mois
    allEvents.forEach(depass => {
      const date = new Date(depass.timestamp);
      const moisKey = date.toISOString().slice(0, 7);
      
      if (!groupes[moisKey]) {
        groupes[moisKey] = {
          key: moisKey,
          nom: date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' }),
          evenements: [],
          coutTotal: 0, // Initialisé à 0
          depassementFacture: null, // L'événement qui sera facturé
        };
      }
      groupes[moisKey].evenements.push(depass);
    });

    // ✅ ÉTAPE 3 : On ajoute les informations de coût depuis "billableExceedances"
    billableEvents.forEach(billable => {
      const date = new Date(billable.timestamp);
      const moisKey = date.toISOString().slice(0, 7);
      
      if (groupes[moisKey]) {
        groupes[moisKey].coutTotal = parseFloat(billable.cout_cmdps_eur || 0);
        groupes[moisKey].depassementFacture = parseFloat(billable.depassement_kw);
      }
    });

    return Object.values(groupes).sort((a, b) => new Date(a.key) - new Date(b.key));
  }, [billableExceedances, allExceedances]);

  return (
    <div className="space-y-3">
      {depassementsGroupes.map((mois) => {
        const isMonthOpen = openMonth === mois.key;
        
        return (
          <div key={mois.key} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <button
              onClick={() => toggleMonth(mois.key)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-4">
                {isMonthOpen ? <FiChevronUp className="text-blue-600" /> : <FiChevronDown className="text-gray-500" />}
                <span className="font-bold text-gray-800">{mois.nom}</span>
                {/* ✅ Affiche maintenant le nombre total d'événements */}
                <span className="text-sm bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                  {mois.evenements.length} événement(s)
                </span>
              </div>
              <div className="text-right">
                <span className="font-bold text-red-600">
                  {mois.coutTotal.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                </span>
                <p className="text-xs text-gray-500">Coût Total du Mois</p>
              </div>
            </button>
            
            {isMonthOpen && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Date</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Période</th>
                      <th className="text-center py-2 px-3 font-semibold text-gray-600">Dépassement (kW)</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Impact Facture Mensuelle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* ✅ Affiche maintenant TOUS les événements */}
                    {mois.evenements
                      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                      .map((event, eventIdx) => {
                        const depassementKw = parseFloat(event.depassement_kw);
                        // L'événement facturé est celui qui correspond au max du mois
                        const isMaxDepassement = depassementKw === mois.depassementFacture;
                        
                        return (
                          <tr key={eventIdx} className={isMaxDepassement ? "bg-red-50" : ""}>
                            <td className="py-2 px-3">{new Date(event.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                            <td className="py-2 px-3">{event.period}</td>
                            <td className="py-2 px-3 text-center font-mono">{depassementKw.toFixed(1)}</td>
                            <td className={`py-2 px-3 text-right font-bold ${isMaxDepassement ? 'text-red-600' : 'text-gray-400'}`}>
                              {isMaxDepassement
                                ? mois.coutTotal.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
                                : '0,00 €'
                              }
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DepassementsParMois;